import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from "recharts";
import {
    BarChart3,
    Wallet,
    Receipt,
    TrendingUp,
    Percent,
    PieChart as PieIcon,
    AlertTriangle,
    RefreshCw,
    Banknote,
    Landmark,
    Clock,
    Users,
    UtensilsCrossed,
} from "lucide-react";
import { OPS } from "@/constants/testIds";
import { PageHeader, UpdatedThroughChip } from "@/components/layout/PageHeader";
import { fmtNumber, fmtCompact } from "@/lib/format";
import { finCurrency, FIN_PROFIT, FIN_LOSS } from "@/lib/finLabels";

const FA = OPS.finAnalytics;

// Brand-aligned palette for the expense breakdown (no purple/pink).
const EXP_PALETTE = [
    "#D1802F", // brand orange
    "#8F340C", // brand brown
    "#2563EB", // calm blue
    "#0EA5A4", // teal
    "#CA8A04", // gold
    "#64748B", // slate
    "#78716C", // warm gray
    "#A8A29E", // light warm gray
];

const AXIS_TICK = { fontSize: 11, fill: "var(--ink-muted)" };
const GRID_COLOR = "var(--border-soft)";

// The ingest stores combined "Arabic / English" labels. Pick the active side.
function pickBilingual(s, lang) {
    if (!s) return "";
    const parts = String(s).split(" / ");
    if (parts.length === 2) return (lang === "ar" ? parts[0] : parts[1]).trim();
    return String(s).trim();
}

const money0 = (v, lang) =>
    v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 0)} ${finCurrency(lang)}`;
const money2 = (v, lang) =>
    v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 2)} ${finCurrency(lang)}`;
const pct1 = (v, lang) =>
    v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 1)}%`;

// Canonical 13-bucket aging order (in days). The chart always shows all 13 in
// this exact order; amounts are looked up from the API payload by bucket name.
const AGING_ORDER = [
    "0-31", "31-60", "61-90", "91-120", "121-150", "151-180",
    "181-210", "211-240", "241-270", "271-300", "301-330", "331-360", "OLDER",
];
const normBucket = (s) => String(s || "").toUpperCase().replace(/\s/g, "").replace(/[\u2013\u2014]/g, "-");
const bucketLabel = (key, t) => (key === "OLDER" ? t("fa_aging_older") : key.replace("-", "\u2013"));

// Food-cost-variance flag -> accent color (high=red, watch=amber, ok=green).
const FLAG_COLORS = { high: "#DC2626", watch: "#D97706", ok: "#16A34A" };
const flagColor = (f) => FLAG_COLORS[f] || "var(--border-strong)";

// ------------------------------------------------------------------ shell card
function SectionCard({ icon: Icon, title, caption, right, children, testId }) {
    return (
        <Card
            data-testid={testId}
            className="fin-fade-up surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]"
        >
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                <div className="flex items-start gap-2.5">
                    {Icon ? (
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[color:rgba(209,128,47,0.12)]">
                            <Icon className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                        </span>
                    ) : null}
                    <div>
                        <CardTitle className="text-lg sm:text-xl font-bold text-[color:var(--ink)]">
                            {title}
                        </CardTitle>
                        {caption ? (
                            <p className="mt-0.5 text-xs sm:text-sm text-[color:var(--ink-muted)]">{caption}</p>
                        ) : null}
                    </div>
                </div>
                {right ? <div className="shrink-0">{right}</div> : null}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

// ------------------------------------------------------------------ KPI cards
function KpiCards({ kpis, lang, t }) {
    const net = kpis?.net_profit;
    const cards = [
        { key: "revenue", title: t("fa_kpi_revenue"), icon: TrendingUp, value: money0(kpis?.revenue, lang), color: "var(--ink)" },
        { key: "expenses", title: t("fa_kpi_expenses"), icon: Receipt, value: money0(kpis?.expenses, lang), color: "var(--ink)" },
        {
            key: "net_profit",
            title: t("fa_kpi_net_profit"),
            icon: Wallet,
            value: money0(net, lang),
            color: net != null && net < 0 ? FIN_LOSS : FIN_PROFIT,
        },
        { key: "margin", title: t("fa_kpi_margin"), icon: Percent, value: pct1(kpis?.profit_margin, lang), color: "var(--ink)" },
    ];

    return (
        <div data-testid={FA.kpiGrid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {cards.map((c) => {
                const Icon = c.icon;
                return (
                    <div
                        key={c.key}
                        data-testid={FA.kpiCard(c.key)}
                        className="fin-fade-up surface border border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)] p-4 sm:p-5 transition-[box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]"
                    >
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-[color:var(--ink-muted)]">
                            <Icon className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            <span className="leading-snug">{c.title}</span>
                        </div>
                        <p className="kpi-number mt-2 text-2xl sm:text-3xl font-extrabold tnum" style={{ color: c.color }}>
                            {c.value}
                        </p>
                    </div>
                );
            })}
        </div>
    );
}

// ------------------------------------------------------------------ metrics strip
function MetricsStrip({ metrics, lang, t }) {
    const items = [
        {
            key: "margin",
            title: t("fa_metric_margin"),
            hint: t("fa_metric_margin_hint"),
            value: pct1(metrics?.profit_margin, lang),
            color: metrics?.profit_margin != null && metrics.profit_margin < 0 ? FIN_LOSS : FIN_PROFIT,
        },
        {
            key: "expense_ratio",
            title: t("fa_metric_expense_ratio"),
            hint: t("fa_metric_expense_ratio_hint"),
            value: pct1(metrics?.expense_to_revenue, lang),
            color: "var(--brand-brown)",
        },
    ];

    return (
        <SectionCard testId={FA.metricsStrip} icon={Percent} title={t("fa_metrics_title")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {items.map((m) => (
                    <div
                        key={m.key}
                        data-testid={FA.metricCard(m.key)}
                        className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3"
                    >
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[color:var(--ink)] truncate">{m.title}</p>
                            <p className="text-xs text-[color:var(--ink-muted)] truncate">{m.hint}</p>
                        </div>
                        <span className="kpi-number text-2xl font-extrabold tnum shrink-0" style={{ color: m.color }}>
                            <bdi dir="ltr">{m.value}</bdi>
                        </span>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

// ------------------------------------------------------------------ expense breakdown
function ExpenseBreakdown({ rows, total, lang, t }) {
    const data = rows.map((r, i) => ({
        key: r.category,
        label: pickBilingual(r.category, lang),
        amount: r.amount || 0,
        pct: r.pct_of_total,
        lines: r.lines,
        color: EXP_PALETTE[i % EXP_PALETTE.length],
    }));

    const Tip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div
                dir={lang === "ar" ? "rtl" : "ltr"}
                className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs"
            >
                <p className="font-semibold text-[color:var(--ink)]">{d.label}</p>
                <p className="tnum text-[color:var(--ink)]">{money2(d.amount, lang)}</p>
                {d.pct != null ? (
                    <p className="tnum text-[color:var(--ink-muted)]">
                        {fmtNumber(d.pct, lang, 1)}% {t("fa_expense_of_total")}
                    </p>
                ) : null}
            </div>
        );
    };

    const chartHeight = Math.max(220, data.length * 52 + 24);

    const right =
        total != null ? (
            <Badge
                variant="secondary"
                className="bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0 tnum"
            >
                <bdi dir="ltr">{money0(total, lang)}</bdi>
            </Badge>
        ) : null;

    return (
        <SectionCard testId={FA.expenseCard} icon={PieIcon} title={t("fa_expense_title")} caption={t("fa_expense_sub")} right={right}>
            {!data.length ? (
                <EmptyState icon={PieIcon} title={t("fa_empty_title")} hint={t("fa_empty_hint")} compact dataset="financials_pl_monthly" />
            ) : (
                <div className="space-y-5">
                    <div dir="ltr" data-testid={FA.expenseChart}>
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart
                                data={data}
                                layout="vertical"
                                barCategoryGap="26%"
                                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                                <XAxis type="number" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                                <YAxis
                                    type="category"
                                    dataKey="label"
                                    width={lang === "ar" ? 150 : 168}
                                    tick={{ fontSize: 12, fill: "var(--ink)" }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip cursor={{ fill: "rgba(186,217,235,0.18)" }} content={<Tip />} />
                                <Bar dataKey="amount" radius={[0, 6, 6, 0]} maxBarSize={30}>
                                    {data.map((d) => (
                                        <Cell key={d.key} fill={d.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="space-y-2">
                        {data.map((d) => (
                            <div
                                key={d.key}
                                data-testid={FA.expenseRow(d.label)}
                                className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-3 py-2.5"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="flex min-w-0 items-center gap-2 text-sm text-[color:var(--ink)]">
                                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                                        <span className="truncate">{d.label}</span>
                                        {d.lines != null ? (
                                            <span className="hidden sm:inline text-xs text-[color:var(--ink-muted)] tnum">
                                                · {fmtNumber(d.lines, lang, 0)} {t("fa_lines")}
                                            </span>
                                        ) : null}
                                    </span>
                                    <span className="flex items-center gap-2 text-xs tnum shrink-0">
                                        <span className="font-medium text-[color:var(--ink)]">{money0(d.amount, lang)}</span>
                                        <span className="min-w-[44px] text-end font-semibold text-[color:var(--brand-brown)]">
                                            {pct1(d.pct, lang)}
                                        </span>
                                    </span>
                                </div>
                                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[color:rgba(224,222,217,0.7)]">
                                    <div className="h-full rounded-full" style={{ width: `${d.pct || 0}%`, background: d.color }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

// ------------------------------------------------------------------ cash on hand
function CashOnHand({ total, banks, lang, t }) {
    const rows = (banks || []).map((b) => ({
        key: b.code || b.account,
        account: pickBilingual(b.account, lang),
        code: b.code,
        balance: b.balance,
    }));

    return (
        <SectionCard testId={FA.cashCard} icon={Banknote} title={t("fa_cash_title")} caption={t("fa_cash_sub")}>
            {!rows.length ? (
                <EmptyState icon={Banknote} title={t("fa_cash_empty")} hint={t("fa_cash_empty_hint")} compact dataset="financial_cash" />
            ) : (
                <div className="space-y-4">
                    {/* header KPI */}
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] bg-[color:rgba(209,128,47,0.08)] px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-[color:var(--ink)]">
                            <Landmark className="h-4 w-4 text-[color:var(--brand-orange)]" />
                            {t("fa_cash_total")}
                        </span>
                        <span data-testid={FA.cashTotal} className="kpi-number text-2xl font-extrabold tnum text-[color:var(--brand-brown)]">
                            <bdi dir="ltr">{money0(total, lang)}</bdi>
                        </span>
                    </div>

                    {/* scrollable account list */}
                    <div
                        data-testid={FA.cashList}
                        className="max-h-[320px] space-y-2 overflow-y-auto scrollbar-thin pe-1"
                    >
                        {rows.map((r) => (
                            <div
                                key={r.key}
                                data-testid={FA.cashRow(r.key)}
                                className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] surface-2 px-3 py-2.5"
                            >
                                <span className="flex min-w-0 items-center gap-2 text-sm text-[color:var(--ink)]">
                                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[color:var(--brand-orange)]" />
                                    <span className="truncate font-medium">{r.account}</span>
                                    {r.code ? (
                                        <span className="hidden shrink-0 sm:inline text-xs text-[color:var(--ink-muted)] tnum">
                                            <bdi dir="ltr">#{r.code}</bdi>
                                        </span>
                                    ) : null}
                                </span>
                                <span className="shrink-0 text-sm font-bold tnum text-[color:var(--ink)]">
                                    <bdi dir="ltr">{money0(r.balance, lang)}</bdi>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

// ------------------------------------------------------------------ receivables aging
function ReceivablesAging({ total, buckets, debtors, lang, t }) {
    const byBucket = new Map((buckets || []).map((b) => [normBucket(b.bucket), b.amount || 0]));
    const data = AGING_ORDER.map((key) => ({
        key,
        label: bucketLabel(key, t),
        amount: byBucket.get(normBucket(key)) || 0,
    }));
    const hasAging = (buckets || []).some((b) => (b.amount || 0) > 0);
    const debtorRows = (debtors || []).slice(0, 10).map((d) => ({
        key: d.customer,
        customer: pickBilingual(d.customer, lang),
        balance: d.balance,
    }));

    const Tip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div
                dir={lang === "ar" ? "rtl" : "ltr"}
                className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs"
            >
                <p className="font-semibold text-[color:var(--ink)]">{d.label}</p>
                <p className="tnum text-[color:var(--ink)]">{money2(d.amount, lang)}</p>
            </div>
        );
    };

    return (
        <SectionCard testId={FA.receivablesCard} icon={Clock} title={t("fa_receivables_title")} caption={t("fa_receivables_sub")}>
            {!hasAging && !debtorRows.length ? (
                <EmptyState icon={Clock} title={t("fa_receivables_empty")} hint={t("fa_receivables_empty_hint")} compact dataset="financial_receivables" />
            ) : (
                <div className="space-y-5">
                    {/* header KPI */}
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] bg-[color:rgba(143,52,12,0.08)] px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-[color:var(--ink)]">
                            <Receipt className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            {t("fa_receivables_total")}
                        </span>
                        <span data-testid={FA.receivablesTotal} className="kpi-number text-2xl font-extrabold tnum text-[color:var(--brand-brown)]">
                            <bdi dir="ltr">{money0(total, lang)}</bdi>
                        </span>
                    </div>

                    {/* aging bar chart (always 13 buckets, in order) */}
                    <div dir="ltr" data-testid={FA.agingChart}>
                        <ResponsiveContainer width="100%" height={Math.max(320, data.length * 30 + 24)}>
                            <BarChart
                                data={data}
                                layout="vertical"
                                barCategoryGap="22%"
                                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                                <XAxis type="number" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                                <YAxis
                                    type="category"
                                    dataKey="label"
                                    width={lang === "ar" ? 86 : 78}
                                    tick={{ fontSize: 11, fill: "var(--ink)" }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <Tooltip cursor={{ fill: "rgba(186,217,235,0.18)" }} content={<Tip />} />
                                <Bar dataKey="amount" radius={[0, 5, 5, 0]} maxBarSize={18} fill="#D1802F" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* top debtors */}
                    {debtorRows.length ? (
                        <div className="space-y-2">
                            <p className="flex items-center gap-2 text-sm font-semibold text-[color:var(--ink)]">
                                <Users className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                {t("fa_top_debtors")}
                            </p>
                            <div data-testid={FA.debtorsList} className="space-y-2">
                                {debtorRows.map((d, i) => (
                                    <div
                                        key={d.key}
                                        data-testid={FA.debtorRow(d.key)}
                                        className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] surface-2 px-3 py-2.5"
                                    >
                                        <span className="flex min-w-0 items-center gap-2 text-sm text-[color:var(--ink)]">
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[color:rgba(143,52,12,0.12)] text-xs font-bold text-[color:var(--brand-brown)] tnum">
                                                {i + 1}
                                            </span>
                                            <span className="truncate font-medium">{d.customer}</span>
                                        </span>
                                        <span className="shrink-0 text-sm font-bold tnum text-[color:var(--ink)]">
                                            <bdi dir="ltr">{money0(d.balance, lang)}</bdi>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </SectionCard>
    );
}

// ------------------------------------------------------------------ food cost variance
function FoodCostVariance({ fcv, lang, t }) {
    const branches = fcv?.branches || [];
    const company = fcv?.company || {};

    return (
        <SectionCard testId={FA.fcvCard} icon={UtensilsCrossed} title={t("fa_fcv_title")} caption={t("fa_fcv_intro")}>
            {!branches.length ? (
                <EmptyState icon={UtensilsCrossed} title={t("fa_fcv_empty")} hint={t("fa_fcv_empty_hint")} compact dataset="financial_food_cost_variance" />
            ) : (
                <div className="space-y-4">
                    {/* header strip: company food cost % + theoretical baseline % */}
                    <div className="flex flex-wrap items-end gap-x-10 gap-y-3 rounded-xl border border-[color:var(--border-soft)] bg-[color:rgba(209,128,47,0.08)] px-4 py-3">
                        <div>
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("fa_fcv_company")}</p>
                            <p data-testid={FA.fcvCompanyPct} className="kpi-number text-3xl font-extrabold tnum text-[color:var(--brand-brown)]">
                                <bdi dir="ltr">{pct1(company.food_cost_pct, lang)}</bdi>
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("fa_fcv_baseline")}</p>
                            <p data-testid={FA.fcvBaseline} className="kpi-number text-2xl font-bold tnum text-[color:var(--ink-muted)]">
                                <bdi dir="ltr">{pct1(fcv.theoretical_pct, lang)}</bdi>
                            </p>
                        </div>
                    </div>

                    {/* ranked table (sorted by excess desc on the server) */}
                    <div className="overflow-x-auto">
                        <Table data-testid={FA.fcvTable}>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="text-start text-[color:var(--ink-muted)]">{t("fa_fcv_col_branch")}</TableHead>
                                    <TableHead className="text-end text-[color:var(--ink-muted)]">{t("fa_fcv_col_revenue")}</TableHead>
                                    <TableHead className="text-end text-[color:var(--ink-muted)]">{t("fa_fcv_col_food")}</TableHead>
                                    <TableHead className="text-end text-[color:var(--ink-muted)]">{t("fa_fcv_col_food_pct")}</TableHead>
                                    <TableHead className="text-end text-[color:var(--ink-muted)]">{t("fa_fcv_col_excess")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {branches.map((b, i) => {
                                    const primary = b.branch_en || b.branch_ar;
                                    const secondary = b.branch_en && b.branch_ar ? b.branch_ar : null;
                                    const ex = b.excess_sar || 0;
                                    const exColor = ex > 0 ? FIN_LOSS : ex < 0 ? FIN_PROFIT : "var(--ink)";
                                    return (
                                        <TableRow key={b.branch_en || b.branch_ar || i} data-testid={FA.fcvRow(b.branch_en || b.branch_ar)}>
                                            <TableCell className="border-s-4 ps-3" style={{ borderInlineStartColor: flagColor(b.flag) }}>
                                                <div className="font-semibold text-[color:var(--ink)]">{primary}</div>
                                                {secondary ? (
                                                    <div className="text-xs text-[color:var(--ink-muted)]">{secondary}</div>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                <bdi dir="ltr">{money0(b.revenue, lang)}</bdi>
                                            </TableCell>
                                            <TableCell className="text-end tnum text-[color:var(--ink)]">
                                                <bdi dir="ltr">{money0(b.food_cost, lang)}</bdi>
                                            </TableCell>
                                            <TableCell className="text-end tnum font-bold text-[color:var(--ink)]">
                                                <bdi dir="ltr">{pct1(b.food_cost_pct, lang)}</bdi>
                                            </TableCell>
                                            <TableCell className="text-end tnum font-semibold" style={{ color: exColor }}>
                                                <bdi dir="ltr">{money0(ex, lang)}</bdi>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {fcv.note ? (
                        <p data-testid={FA.fcvNote} className="text-xs italic text-[color:var(--ink-muted)]">
                            {pickBilingual(fcv.note, lang)}
                        </p>
                    ) : null}
                </div>
            )}
        </SectionCard>
    );
}

// ------------------------------------------------------------------ skeleton
function LoadingState() {
    return (
        <div className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-[112px] rounded-2xl" />
                ))}
            </div>
            <Skeleton className="h-[120px] rounded-2xl" />
            <Skeleton className="h-[360px] rounded-2xl" />
        </div>
    );
}

// ------------------------------------------------------------------ page
export default function FinancialsAnalyticsPage() {
    const { t, lang } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(() => {
        let active = true;
        setLoading(true);
        setError(false);
        api
            .get("/financial-analytics/overview")
            .then((r) => active && setData(r.data))
            .catch(() => active && setError(true))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => load(), [load]);

    const period = data?.period ? pickBilingual(data.period, lang) : null;
    const expRows = data?.expenses_by_category || [];

    return (
        <div data-testid={FA.page} className="fin-scope space-y-5 sm:space-y-6">
            <PageHeader
                icon={BarChart3}
                title={t("fa_title")}
                subtitle={t("fa_subtitle")}
                meta={
                    period ? (
                        <UpdatedThroughChip label={t("period") || null} value={period} />
                    ) : null
                }
            />

            {loading ? (
                <LoadingState />
            ) : error ? (
                <Card data-testid={FA.errorState} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                    <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[color:rgba(180,35,24,0.12)]">
                            <AlertTriangle className="h-6 w-6 text-[color:var(--danger)]" />
                        </span>
                        <p className="text-sm font-medium text-[color:var(--ink)]">{t("fa_load_error")}</p>
                        <Button
                            data-testid={FA.retry}
                            onClick={load}
                            variant="outline"
                            className="gap-2 border-[color:var(--brand-orange)] text-[color:var(--brand-brown)]"
                        >
                            <RefreshCw className="h-4 w-4" />
                            {t("fa_retry")}
                        </Button>
                    </CardContent>
                </Card>
            ) : !data?.has_data ? (
                <Card data-testid={FA.emptyState} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                    <CardContent className="py-4">
                        <EmptyState icon={BarChart3} title={t("fa_empty_title")} hint={t("fa_empty_hint")} dataset="financials_pl_monthly" />
                    </CardContent>
                </Card>
            ) : (
                <>
                    <KpiCards kpis={data.kpis} lang={lang} t={t} />
                    <MetricsStrip metrics={data.metrics} lang={lang} t={t} />
                    <ExpenseBreakdown rows={expRows} total={data.expenses_total_breakdown} lang={lang} t={t} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
                        <CashOnHand total={data.cash_total} banks={data.cash_banks} lang={lang} t={t} />
                        <ReceivablesAging
                            total={data.receivables_total}
                            buckets={data.aging_buckets}
                            debtors={data.top_debtors}
                            lang={lang}
                            t={t}
                        />
                    </div>
                    <FoodCostVariance fcv={data.food_cost_variance} lang={lang} t={t} />
                </>
            )}
        </div>
    );
}
